/**
 * Live-capture controls + the auto-halt banner (admin only).
 *
 * Reads/writes GET|PUT /api/admin/config — the ALLOWLISTED ops/spend keys:
 *   - live_capture_enabled: the BILLING kill-switch (supervision + STT).
 *   - daily_cost_alert_cents / daily_cost_kill_cents: the two-threshold daily
 *     spend guard (edited here in dollars; validated alert <= kill on both
 *     client and server).
 *   - live_capture_hours: optional daily capture window ({start,end} HH:MM;
 *     the SERVER stamps the shop timezone — the UI only displays it).
 *
 * CRITICAL UX distinction this section exists to make unmistakable:
 *   'Live coaching' (the master switch above) only turns the on-screen NUDGES
 *   on/off — calls are still supervised + transcribed and billing continues.
 *   'Live capture' (here) turns the supervision + transcription itself off —
 *   that is what stops the spend. Capture-off applies to NEW calls only.
 *
 * HaltBanner renders when either kill-switch is off (the daily-spend auto-halt
 * flips both processing_enabled and live_capture_enabled to false) and offers a
 * one-click re-enable WITH the raise-the-cap-first warning: after a same-day
 * re-enable the auto-halt will NOT fire again until the next local day.
 *
 * Both components share the same query key so the banner and the form stay in
 * sync through one cache entry.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiRequestError, type AdminOpsConfigUpdate } from "@/lib/api";
import { Button, Card } from "@/components/ui";
import { LoadingState } from "@/components/LoadingState";
import { ErrorState } from "@/components/ErrorState";
import { SavedNote } from "./shared";
import { centsToDollars } from "@/lib/format";

const OPS_KEY = ["admin", "ops-config"] as const;

/** Cents → editable dollars string ("10.00"). */
function centsToInput(cents: number): string {
  return (cents / 100).toFixed(2);
}

/** Dollars input → integer cents; null when not a valid non-negative amount. */
function inputToCents(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

/** The same toggle-switch affordance the Live coaching master switch uses. */
function Switch({
  checked,
  onToggle,
  label,
}: {
  checked: boolean;
  onToggle: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={onToggle}
      className={
        "relative inline-flex h-6 w-11 items-center rounded-full transition " +
        (checked ? "bg-primary" : "bg-muted")
      }
    >
      <span
        className={
          "inline-block h-4 w-4 transform rounded-full bg-background transition " +
          (checked ? "translate-x-6" : "translate-x-1")
        }
      />
    </button>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Auto-halt banner                                                            */
/* ────────────────────────────────────────────────────────────────────────── */

/**
 * Banner when processing and/or live capture is switched OFF. The ops config
 * carries no halted-by field, so the banner infers the situation from WHICH
 * switches are off:
 *   - processing OFF (the auto-halt always flips BOTH switches) → the red
 *     emergency banner: likely the daily-spend guard fired; one-click
 *     re-enables both, with the raise-the-cap-first warning front and center.
 *   - ONLY live capture OFF → that's the form below, i.e. a deliberate manual
 *     pause (the auto-halt never leaves processing on) — a calm amber notice,
 *     not a standing alarm, with a capture-only re-enable.
 * Renders nothing while loading or when everything is on (the section below
 * owns its own loading/error states).
 */
export function HaltBanner() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: OPS_KEY,
    queryFn: ({ signal }) => api.adminGetOpsConfig(signal),
  });

  const reEnable = useMutation({
    mutationFn: (body: AdminOpsConfigUpdate) => api.adminSaveOpsConfig(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: OPS_KEY }),
  });

  const d = query.data;
  if (!d || (d.processing_enabled && d.live_capture_enabled)) return null;

  // Manual capture pause: processing still on. (The auto-halt flips both off,
  // so this state can only come from the switch below — don't alarm over it.)
  if (d.processing_enabled && !d.live_capture_enabled) {
    return (
      <div
        role="status"
        className="rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/40"
      >
        <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
          Live capture is switched off
        </p>
        <p className="mt-1 text-sm text-amber-800/90 dark:text-amber-300/90">
          New calls aren&apos;t being supervised or transcribed, so there&apos;s no
          live-call spend. Call processing is still on. If this pause wasn&apos;t
          deliberate, re-enable capture — spending resumes for new calls.
        </p>
        <div className="mt-3 flex items-center gap-3">
          <Button
            onClick={() => reEnable.mutate({ live_capture_enabled: true })}
            disabled={reEnable.isPending}
          >
            {reEnable.isPending ? "Re-enabling…" : "Re-enable live capture"}
          </Button>
          {reEnable.isError && (
            <span className="text-sm text-amber-800 dark:text-amber-300">
              Couldn&apos;t re-enable. Please try again.
            </span>
          )}
        </div>
      </div>
    );
  }

  const offSwitches = [
    !d.processing_enabled ? "call processing" : null,
    !d.live_capture_enabled ? "live capture" : null,
  ]
    .filter(Boolean)
    .join(" and ");

  return (
    <div
      role="alert"
      className="rounded-lg border border-red-300 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950/40"
    >
      <p className="text-sm font-semibold text-red-800 dark:text-red-300">
        Spending is halted — {offSwitches} {offSwitches.includes("and") ? "are" : "is"}{" "}
        switched OFF
      </p>
      <p className="mt-1 text-sm text-red-700 dark:text-red-300/90">
        If today&apos;s spend crossed the auto-halt cap (
        {centsToDollars(d.daily_cost_kill_cents)}/day), the spend guard turned these off
        automatically. No new calls are being processed or captured until they&apos;re
        re-enabled.
      </p>
      <p className="mt-2 text-sm font-medium text-red-800 dark:text-red-300">
        Before re-enabling: after a same-day re-enable the auto-halt will NOT fire again
        until the next local day. If you expect more spend today, raise the auto-halt
        cap in the spend guard below FIRST.
      </p>
      <div className="mt-3 flex items-center gap-3">
        <Button
          onClick={() =>
            reEnable.mutate({ processing_enabled: true, live_capture_enabled: true })
          }
          disabled={reEnable.isPending}
          className="bg-red-600 text-white hover:opacity-90"
        >
          {reEnable.isPending ? "Re-enabling…" : "Re-enable processing + capture"}
        </Button>
        {reEnable.isError && (
          <span className="text-sm text-red-700 dark:text-red-300">
            Couldn&apos;t re-enable. Please try again.
          </span>
        )}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Live capture & spend guard                                                  */
/* ────────────────────────────────────────────────────────────────────────── */

export function LiveCaptureSection() {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: OPS_KEY,
    queryFn: ({ signal }) => api.adminGetOpsConfig(signal),
  });

  const [captureEnabled, setCaptureEnabled] = useState(true);
  const [alertDollars, setAlertDollars] = useState("");
  const [killDollars, setKillDollars] = useState("");
  const [hoursEnabled, setHoursEnabled] = useState(false);
  const [hoursStart, setHoursStart] = useState("");
  const [hoursEnd, setHoursEnd] = useState("");
  const [justSaved, setJustSaved] = useState(false);

  const data = query.data;
  useEffect(() => {
    if (data) {
      setCaptureEnabled(data.live_capture_enabled);
      setAlertDollars(centsToInput(data.daily_cost_alert_cents));
      setKillDollars(centsToInput(data.daily_cost_kill_cents));
      setHoursEnabled(data.live_capture_hours !== null);
      setHoursStart(data.live_capture_hours?.start ?? "");
      setHoursEnd(data.live_capture_hours?.end ?? "");
      setJustSaved(false);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: (body: AdminOpsConfigUpdate) => api.adminSaveOpsConfig(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: OPS_KEY });
      setJustSaved(true);
    },
  });

  if (query.isLoading) {
    return <LoadingState label="Loading live-capture settings…" />;
  }
  if (query.isError || !data) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  function touch() {
    if (justSaved) setJustSaved(false);
  }

  // ── Validation (mirrors the server: alert <= kill; 0 disables a tier) ─────
  const alertCents = inputToCents(alertDollars);
  const killCents = inputToCents(killDollars);
  const thresholdsInvalid = alertCents === null || killCents === null;
  const alertAboveKill =
    alertCents !== null && killCents !== null && alertCents > 0 && killCents > 0 && alertCents > killCents;
  const hoursInvalid = hoursEnabled && (hoursStart === "" || hoursEnd === "");

  const nextHours = hoursEnabled ? { start: hoursStart, end: hoursEnd } : null;
  const serverHours = data.live_capture_hours
    ? { start: data.live_capture_hours.start, end: data.live_capture_hours.end }
    : null;
  const hoursDirty =
    (nextHours === null) !== (serverHours === null) ||
    nextHours?.start !== serverHours?.start ||
    nextHours?.end !== serverHours?.end;

  const dirty =
    captureEnabled !== data.live_capture_enabled ||
    alertCents !== data.daily_cost_alert_cents ||
    killCents !== data.daily_cost_kill_cents ||
    hoursDirty;
  const canSave = dirty && !thresholdsInvalid && !alertAboveKill && !hoursInvalid && !save.isPending;

  // Server-side rejection (e.g. alert_threshold_above_kill) → show its message.
  const saveErrMsg =
    save.error instanceof ApiRequestError
      ? save.error.body?.error === "alert_threshold_above_kill"
        ? "The warning threshold must be at or below the auto-halt cap."
        : "Couldn't save your changes. Please try again."
      : save.error
        ? "Couldn't save your changes. Please try again."
        : null;

  function saveAll() {
    if (alertCents === null || killCents === null) return;
    setJustSaved(false);
    save.mutate({
      live_capture_enabled: captureEnabled,
      daily_cost_alert_cents: alertCents,
      daily_cost_kill_cents: killCents,
      live_capture_hours: nextHours,
    });
  }

  return (
    <div className="flex flex-col gap-4 rounded-lg border-2 border-dashed p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Live capture &amp; spend guard</h2>
          <p className="text-sm text-muted-foreground">
            Controls the supervision + transcription itself — this is the billing
            switch, separate from the nudge switch above.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <SavedNote show={justSaved && !dirty} />
          <Button onClick={saveAll} disabled={!canSave}>
            {save.isPending ? "Saving…" : "Save capture settings"}
          </Button>
        </div>
      </div>

      {/* The two-switches explainer — the distinction must be unmistakable. */}
      <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-950/40">
        <p className="font-medium text-amber-800 dark:text-amber-300">
          Two different switches — don&apos;t mix them up
        </p>
        <ul className="mt-1 list-disc pl-5 text-amber-800/90 dark:text-amber-300/90">
          <li>
            <span className="font-medium">Live coaching</span> (the master switch above)
            only turns the on-screen nudges on/off. With it off, calls are STILL
            supervised and transcribed — STT and AI billing continues.
          </li>
          <li>
            <span className="font-medium">Live capture</span> (below) turns the
            supervision + transcription itself off. That is what stops the spend.
          </li>
          <li>
            Turning capture off stops billing for <span className="font-medium">NEW
            calls only</span> — a call already being captured finishes normally.
          </li>
        </ul>
      </div>

      {/* Capture switch */}
      <Card className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-medium">Live capture (supervision + STT billing)</h3>
          <p className="text-sm text-muted-foreground">
            Off = no supervise legs, no transcription, no live-call billing for new
            calls. The daily-spend auto-halt also flips this off.
          </p>
        </div>
        <Switch
          checked={captureEnabled}
          label="Live capture"
          onToggle={() => {
            touch();
            setCaptureEnabled((v) => !v);
          }}
        />
      </Card>

      {/* Spend thresholds */}
      <Card className="flex flex-col gap-3">
        <h3 className="text-sm font-medium">Daily spend guard</h3>
        <p className="text-sm text-muted-foreground">
          Watches today&apos;s total spend (Claude + speech-to-text, shop-local day).
          Set a tier to $0 to disable it.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Warning alert ($/day)</span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={alertDollars}
              onChange={(e) => {
                touch();
                setAlertDollars(e.target.value);
              }}
              className="rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
            <span className="text-xs text-muted-foreground">
              Sends a Telegram warning once per day when crossed. Nothing is switched
              off.
            </span>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Auto-halt cap ($/day)</span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={killDollars}
              onChange={(e) => {
                touch();
                setKillDollars(e.target.value);
              }}
              className="rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
            <span className="text-xs text-muted-foreground">
              When crossed, processing AND live capture are switched off automatically
              (critical alert). They stay off until re-enabled here.
            </span>
          </label>
        </div>
        {thresholdsInvalid && (
          <p className="text-sm text-amber-600 dark:text-amber-400">
            Both thresholds need a valid dollar amount ($0 or more).
          </p>
        )}
        {alertAboveKill && (
          <p className="text-sm text-amber-600 dark:text-amber-400">
            The warning threshold must be at or below the auto-halt cap — otherwise the
            warning would never fire before the halt. (Set either to $0 to disable that
            tier.)
          </p>
        )}
      </Card>

      {/* Capture hours */}
      <Card className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-medium">Capture hours</h3>
            <p className="text-sm text-muted-foreground">
              Limit live capture (and its billing) to a daily window. Off = capture
              around the clock.
            </p>
          </div>
          <Switch
            checked={hoursEnabled}
            label="Limit capture to a daily window"
            onToggle={() => {
              touch();
              setHoursEnabled((v) => !v);
            }}
          />
        </div>
        {hoursEnabled && (
          <>
            <div className="flex flex-wrap items-end gap-3">
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium">Start</span>
                <input
                  type="time"
                  value={hoursStart}
                  onChange={(e) => {
                    touch();
                    setHoursStart(e.target.value);
                  }}
                  className="rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium">End</span>
                <input
                  type="time"
                  value={hoursEnd}
                  onChange={(e) => {
                    touch();
                    setHoursEnd(e.target.value);
                  }}
                  className="rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
                />
              </label>
              <p className="pb-1.5 text-sm text-muted-foreground">
                Times are in <span className="font-medium">{data.timezone}</span> (the
                shop&apos;s timezone).
              </p>
            </div>
            <p className="text-xs text-muted-foreground">
              Outside the window, NEW calls aren&apos;t captured (no billing); a call
              already in progress is never cut off at the boundary. An overnight window
              (e.g. 19:00 → 07:00) is supported.
            </p>
            {hoursInvalid && (
              <p className="text-sm text-amber-600 dark:text-amber-400">
                Set both a start and an end time (or turn the window off for 24/7
                capture).
              </p>
            )}
          </>
        )}
      </Card>

      {saveErrMsg && <p className="text-sm text-red-600">{saveErrMsg}</p>}
    </div>
  );
}
